import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { BottomSheet } from '@/components/BottomSheet';
import { Input } from '@/components/primitives/Input';
import { Button } from '@/components/primitives/Button';
import { SegmentedControl } from '@/components/primitives/SegmentedControl';
import { Toggle } from '@/components/primitives/Toggle';
import { useStorages, useCreateStorage, useUpdateStorage, useTestStorageForm, } from '@/api/useStorages';
import { ConnectionTestStrip } from '@/components/ConnectionTestResult';
import s from './StorageFormSheet.module.css';
const DEFAULT_STATE = {
    name: '',
    kind: 'minio',
    endpoint_url: '',
    region: '',
    bucket: '',
    access_key: '',
    secret_key: '',
    retention_days_default: 30,
    enabled: true,
};
export function StorageFormSheet({ mode, storageId, onClose }) {
    const storages = useStorages();
    const create = useCreateStorage();
    const update = useUpdateStorage(storageId ?? '');
    const test = useTestStorageForm();
    const [form, setForm] = useState(DEFAULT_STATE);
    const [err, setErr] = useState(null);
    const [seeded, setSeeded] = useState(false);
    useEffect(() => {
        if (mode === 'edit' && !seeded && storages.data) {
            const st = storages.data.find((x) => x.id === storageId);
            if (st) {
                setForm({
                    name: st.name,
                    kind: st.kind,
                    endpoint_url: st.endpoint_url,
                    region: st.region ?? '',
                    bucket: st.bucket,
                    access_key: '',
                    secret_key: '',
                    retention_days_default: st.retention_days_default,
                    enabled: st.enabled,
                });
                setSeeded(true);
            }
        }
    }, [mode, seeded, storages.data, storageId]);
    const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const runTest = () => {
        test.reset();
        test.mutate({
            kind: form.kind,
            endpoint_url: form.endpoint_url,
            region: form.region || 'us-east-1',
            bucket: form.bucket,
            access_key: form.access_key,
            secret_key: form.secret_key,
        });
    };
    const submit = () => {
        setErr(null);
        const payload = {
            name: form.name,
            kind: form.kind,
            endpoint_url: form.endpoint_url,
            region: form.region || null,
            bucket: form.bucket,
            access_key: form.access_key,
            secret_key: form.secret_key,
            retention_days_default: form.retention_days_default,
            enabled: form.enabled,
        };
        const onError = (e) => setErr(e.message);
        if (mode === 'create')
            create.mutate(payload, { onSuccess: onClose, onError });
        else
            update.mutate(payload, { onSuccess: onClose, onError });
    };
    return (_jsxs(BottomSheet, { open: true, title: mode === 'create' ? 'Add storage' : 'Edit storage', onClose: onClose, footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", onClick: onClose, children: "Cancel" }), _jsx(Button, { onClick: submit, disabled: create.isPending || update.isPending, children: mode === 'create' ? 'Create storage' : 'Save changes' })] }), children: [err && _jsx("div", { className: s.errBlock, children: err }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "st-name", children: "Name" }), _jsx(Input, { id: "st-name", value: form.name, onChange: (e) => setF('name', e.target.value), placeholder: "primary-minio" })] }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, children: "Kind" }), _jsx(SegmentedControl, { value: form.kind, onChange: (v) => setF('kind', v), options: [
                            { value: 'minio', label: 'MinIO' },
                            { value: 's3', label: 'AWS S3' },
                        ] })] }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "st-endpoint", children: "Endpoint URL" }), _jsx(Input, { id: "st-endpoint", mono: true, value: form.endpoint_url, onChange: (e) => setF('endpoint_url', e.target.value), placeholder: form.kind === 's3' ? 'https://s3.amazonaws.com' : 'https://minio.internal:9000' })] }), _jsxs("div", { className: s.cols2, children: [form.kind === 's3' && (_jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "st-region", children: "Region" }), _jsx(Input, { id: "st-region", value: form.region, onChange: (e) => setF('region', e.target.value), placeholder: "us-east-1" })] })), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "st-bucket", children: "Bucket" }), _jsx(Input, { id: "st-bucket", mono: true, value: form.bucket, onChange: (e) => setF('bucket', e.target.value) })] })] }), _jsxs("div", { className: s.cols2, children: [_jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "st-akey", children: "Access key" }), _jsx(Input, { id: "st-akey", mono: true, value: form.access_key, onChange: (e) => setF('access_key', e.target.value), placeholder: mode === 'edit' ? 'unchanged' : '' })] }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "st-skey", children: "Secret key" }), _jsx(Input, { id: "st-skey", type: "password", value: form.secret_key, onChange: (e) => setF('secret_key', e.target.value), placeholder: mode === 'edit' ? 'unchanged' : '' })] })] }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "st-ret", children: "Default retention (days)" }), _jsx(Input, { id: "st-ret", type: "number", value: form.retention_days_default, onChange: (e) => setF('retention_days_default', Number(e.target.value)) })] }), _jsxs("div", { className: s.optsRow, children: [_jsx("span", { className: s.optsLabel, children: "Enabled" }), _jsx(Toggle, { on: form.enabled, onChange: (v) => setF('enabled', v), "aria-label": "Enabled" })] }), _jsxs("div", { className: s.field, style: { display: 'flex', flexDirection: 'column', gap: 10 }, children: [_jsx("div", { children: _jsx(Button, { variant: "ghost", onClick: runTest, disabled: test.isPending ||
                                !form.endpoint_url ||
                                !form.bucket ||
                                !form.access_key ||
                                !form.secret_key, children: test.isPending ? 'Testing…' : 'Test reachability' }) }), (test.isPending || test.data || test.error) && (_jsx(ConnectionTestStrip, { pending: test.isPending, result: test.data
                            ? test.data
                            : test.error
                                ? { ok: false, latency_ms: 0, error: test.error.message }
                                : null }))] })] }));
}
